// src/utils/walletService.js
import { 
  doc, 
  updateDoc, 
  increment, 
  addDoc, 
  collection, 
  serverTimestamp,
  getDoc,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Wallet Service - Utility functions for wallet operations
 */
export class WalletService {
  
  /**
   * Add balance to user's wallet
   * @param {string} userId - User ID
   * @param {number} amount - Amount to add
   * @param {string} reason - Reason for adding balance
   * @param {string} category - Category of transaction (competition_reward, idea_reward, bonus)
   * @returns {Promise<string>} Transaction ID
   */
  static async addBalance(userId, amount, reason, category = 'reward') {
    try {
      return await runTransaction(db, async (transaction) => {
        // Get user document
        const userRef = doc(db, 'users', userId);
        const userSnap = await transaction.get(userRef);
        
        if (!userSnap.exists()) {
          throw new Error('User not found');
        }
        
        const currentBalance = userSnap.data().rewardsBalance || 0;
        const newBalance = currentBalance + amount;
        
        // Update user balance
        transaction.update(userRef, {
          rewardsBalance: newBalance,
          lastBalanceUpdate: serverTimestamp()
        });
        
        // Create transaction record
        const transactionRef = doc(collection(db, 'transactions'));
        transaction.set(transactionRef, {
          userId: userId,
          type: 'credit',
          amount: amount,
          balanceBefore: currentBalance,
          balanceAfter: newBalance,
          description: reason,
          category: category,
          status: 'completed',
          createdAt: serverTimestamp()
        });
        
        return transactionRef.id;
      });
    } catch (error) {
      console.error('Error adding balance:', error);
      throw new Error('Failed to add balance: ' + error.message);
    }
  }
  
  /**
   * Deduct balance from user's wallet
   * @param {string} userId - User ID
   * @param {number} amount - Amount to deduct
   * @param {string} reason - Reason for deducting balance
   * @param {string} category - Category of transaction (payout, fee)
   * @param {object} accountInfo - Optional account information
   * @returns {Promise<string>} Transaction ID
   */
  static async deductBalance(userId, amount, reason, category = 'payout', accountInfo = null) {
    try {
      return await runTransaction(db, async (transaction) => {
        // Get user document
        const userRef = doc(db, 'users', userId);
        const userSnap = await transaction.get(userRef);
        
        if (!userSnap.exists()) {
          throw new Error('User not found');
        }
        
        const currentBalance = userSnap.data().rewardsBalance || 0;
        
        if (currentBalance < amount) {
          throw new Error('Insufficient balance');
        }
        
        const newBalance = currentBalance - amount;
        
        // Update user balance
        transaction.update(userRef, {
          rewardsBalance: newBalance,
          lastBalanceUpdate: serverTimestamp()
        });
        
        // Create transaction record
        const transactionRef = doc(collection(db, 'transactions'));
        const transactionData = {
          userId: userId,
          type: 'debit',
          amount: amount,
          balanceBefore: currentBalance,
          balanceAfter: newBalance,
          description: reason,
          category: category,
          status: 'completed',
          createdAt: serverTimestamp()
        };

        // Add account info if provided
        if (accountInfo && accountInfo.accountNumber) {
          transactionData.accountNumber = accountInfo.accountNumber;
          transactionData.bankName = accountInfo.bankName || '';
        }

        transaction.set(transactionRef, transactionData);
        
        return transactionRef.id;
      });
    } catch (error) {
      console.error('Error deducting balance:', error);
      throw new Error('Failed to deduct balance: ' + error.message);
    }
  }
  
  /**
   * Get user's current balance
   * @param {string} userId - User ID
   * @returns {Promise<number>} Current balance
   */
  static async getBalance(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        return 0;
      }
      
      return userSnap.data().rewardsBalance || 0;
    } catch (error) {
      console.error('Error getting balance:', error);
      return 0;
    }
  }
  
  /**
   * Process payout request
   * @param {string} payoutRequestId - Payout request ID
   * @param {string} status - New status (approved, rejected)
   * @param {string} adminId - Admin user ID
   * @param {string} notes - Processing notes
   * @returns {Promise<void>}
   */
  static async processPayoutRequest(payoutRequestId, status, adminId, notes = '') {
    try {
      return await runTransaction(db, async (transaction) => {
        // Get payout request
        const payoutRef = doc(db, 'payout_requests', payoutRequestId);
        const payoutSnap = await transaction.get(payoutRef);
        
        if (!payoutSnap.exists()) {
          throw new Error('Payout request not found');
        }
        
        const payoutData = payoutSnap.data();
        
        if (payoutData.status !== 'pending') {
          throw new Error('Payout request already processed');
        }
        
        // Update payout request status
        transaction.update(payoutRef, {
          status: status,
          processedAt: serverTimestamp(),
          processedBy: adminId,
          processingNotes: notes
        });
        
        if (status === 'approved') {
          // Deduct balance from user
          const userRef = doc(db, 'users', payoutData.userId);
          const userSnap = await transaction.get(userRef);
          
          if (!userSnap.exists()) {
            throw new Error('User not found');
          }
          
          const currentBalance = userSnap.data().rewardsBalance || 0;
          
          if (currentBalance < payoutData.amount) {
            throw new Error('Insufficient balance');
          }
          
          const newBalance = currentBalance - payoutData.amount;
          
          // Update user balance
          transaction.update(userRef, {
            rewardsBalance: newBalance,
            lastBalanceUpdate: serverTimestamp()
          });
          
          // Create transaction record
          const transactionRef = doc(collection(db, 'transactions'));
          transaction.set(transactionRef, {
            userId: payoutData.userId,
            type: 'debit',
            amount: payoutData.amount,
            balanceBefore: currentBalance,
            balanceAfter: newBalance,
            description: `Payout to bank account ending in ${payoutData.bankDetails.accountNumber.slice(-4)}`,
            category: 'payout',
            status: 'completed',
            payoutRequestId: payoutRequestId,
            accountNumber: payoutData.bankDetails.accountNumber,
            bankName: payoutData.bankDetails.bankName || '',
            createdAt: serverTimestamp()
          });
        }
      });
    } catch (error) {
      console.error('Error processing payout request:', error);
      throw new Error('Failed to process payout request: ' + error.message);
    }
  }
  
  /**
   * Award competition prize
   * @param {string} userId - Winner user ID
   * @param {number} amount - Prize amount
   * @param {string} competitionTitle - Competition title
   * @param {string} position - Position (1st, 2nd, 3rd, etc.)
   * @returns {Promise<string>} Transaction ID
   */
  static async awardCompetitionPrize(userId, amount, competitionTitle, position = '1st') {
    const reason = `${position} place in "${competitionTitle}" competition`;
    return this.addBalance(userId, amount, reason, 'competition_reward');
  }
  
  /**
   * Award idea reward
   * @param {string} userId - User ID
   * @param {number} amount - Reward amount
   * @param {string} ideaTitle - Idea title
   * @returns {Promise<string>} Transaction ID
   */
  static async awardIdeaReward(userId, amount, ideaTitle) {
    const reason = `Reward for idea: "${ideaTitle}"`;
    return this.addBalance(userId, amount, reason, 'idea_reward');
  }
  
  /**
   * Award bonus
   * @param {string} userId - User ID
   * @param {number} amount - Bonus amount
   * @param {string} reason - Bonus reason
   * @returns {Promise<string>} Transaction ID
   */
  static async awardBonus(userId, amount, reason) {
    return this.addBalance(userId, amount, reason, 'bonus');
  }
  
  /**
   * Calculate processing fee
   * @param {number} amount - Payout amount
   * @param {number} feePercentage - Fee percentage (default 2%)
   * @returns {number} Processing fee
   */
  static calculateProcessingFee(amount, feePercentage = 2) {
    return Math.round((amount * feePercentage) / 100);
  }
  
  /**
   * Validate payout amount
   * @param {number} amount - Amount to validate
   * @param {number} userBalance - User's current balance
   * @param {number} minAmount - Minimum payout amount
   * @param {number} maxAmount - Maximum payout amount
   * @returns {Object} Validation result
   */
  static validatePayoutAmount(amount, userBalance, minAmount = 100, maxAmount = 50000) {
    const errors = [];
    
    if (!amount || amount <= 0) {
      errors.push('Amount must be greater than 0');
    }
    
    if (amount < minAmount) {
      errors.push(`Minimum payout amount is ₹${minAmount}`);
    }
    
    if (amount > maxAmount) {
      errors.push(`Maximum payout amount is ₹${maxAmount}`);
    }
    
    if (amount > userBalance) {
      errors.push('Amount exceeds available balance');
    }
    
    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }
  
  /**
   * Format currency amount
   * @param {number} amount - Amount to format
   * @param {string} currency - Currency code (default 'INR')
   * @returns {string} Formatted currency string
   */
  static formatCurrency(amount, currency = 'INR') {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
  
  /**
   * Get transaction status color
   * @param {string} status - Transaction status
   * @returns {string} Color code
   */
  static getStatusColor(status) {
    const colors = {
      'completed': '#4ade80',
      'pending': '#fbbf24',
      'processing': '#fbbf24',
      'failed': '#f87171',
      'rejected': '#f87171',
      'approved': '#4ade80'
    };
    
    return colors[status] || '#94a3b8';
  }
}

export default WalletService;